import { Component, createRef } from "react"

/* Components */
import { DataLayout, Pagination, Icon, Avatar } from "../../components"

/* Widgets */
import { ReviewModal, ReviewForm, Stars } from "../../widgets/review"
import { UserModal } from "../../widgets/user"

/* Shared */
import { RATE_WORDS, COMPLAINT_STATUSES, rateAccentOf, imagesOf, photoOf, authorOf, ratingOf } from "../../widgets/review/shared"
import { ago, initialsOf, avatarColorOf } from "../../widgets/user/shared"
import { organizationIcon } from "../../widgets/organization/shared"
import { plural } from "../../widgets/category/shared"

/* Constants */
import { env } from "../../constants"

/* REST API */
import { list } from "../../api/Review"


/* The orders of the list */
const SORTS = [
    { key: "new", text: "Новые" },
    { key: "old", text: "Старые" },
    { key: "low", text: "Сначала плохие" },
    { key: "high", text: "Сначала хорошие" }
]

/* The views of the list */
const VIEWS = [
    { key: "all", text: "Все" },
    { key: "unanswered", text: "Без ответа" },
    { key: "answered", text: "С ответом" },
    { key: "photos", text: "С фото" },
    { key: "complaints", text: "Жалобы" }
]

/* Who the review is from */
const SOURCES = [
    { key: "all", text: "Все авторы" },
    { key: "client", text: "Клиенты" },
    { key: "manual", text: "Вручную" }
]

/* The empty filters */
const INITIAL = { search: "", view: "all", source: "all", rate: "", organization: null, sort: "new" }


/* Page Reviews — what the clients say about the points. The page reads the mood of the platform
   (the rating, its trend and the spread of the rates), shows the points whose clients are unhappy
   and decides the complaints the points send about the reviews */
class Reviews extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            limit: 30,
            page: 1,
            statistics: null,
            distribution: [],
            attention: [],
            ...INITIAL
        }

        this._review = createRef()
        this._form = createRef()
        this._user = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(1, true)
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    load = (page = 1, reload = false) => {

        /* Fields */
        const { search, view, source, rate, organization, sort } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        this.setState({ page })

        const body = { page, search, view, source, rate, sort, organizationID: organization ? organization.id : 0 }

        list(body).then(response => {
            if (response && response.status === 200) {

                const fields = {
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    limit: response.data.limit,
                    statistics: response.data.statistics,
                    distribution: response.data.distribution,
                    attention: response.data.attention
                }

                /* The point of the filter comes back with its rating */
                if (response.data.organization) {
                    fields.organization = response.data.organization
                }

                this.setState(fields)
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })
    }

    /* Every filter starts from the first page */
    refilter = fields => {
        this.setState(fields, () => this.load(1, true))
    }

    /* The search waits for the hand to stop */
    searchText = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        this._timer = setTimeout(() => this.load(1, true), text.length === 0 ? 0 : 450)
    }

    /* A filter pressed twice lets go */
    toggle = (field, value, empty) => {
        this.refilter({ [field]: this.state[field] === value ? empty : value })
    }

    /* Show only the reviews of one point */
    pickOrganization = (event, organization) => {

        if (event) {
            event.stopPropagation()
        }

        window.scrollTo(0, 0)
        this.refilter({ organization: { id: organization.id, name: organization.name, image: organization.image } })
    }

    /* The card of the client opens over the review */
    openClient = review => {
        this._user.current.open({ id: review.userID, name: review.userName, image: review.userImage, phone: review.userPhone, active: review.userActive, kind: "app" })
    }

    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Отзывы</div>

                <div className="organization-header-tools">

                    <form onSubmit={event => { event.preventDefault(); clearTimeout(this._timer); this.load(1, true) }} className="organization-search service-search">
                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>
                        <input placeholder="Текст, клиент, мастер или точка" value={search} onChange={event => this.searchText(event.target.value)} />
                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }
                    </form>

                    <div onClick={() => this._form.current.open(null)} className="organization-create">
                        <Icon name="plus" size={17} width={2.4} />
                        <span>Добавить</span>
                    </div>

                </div>

            </div>
        )
    }

    /* The mood of the platform — the rating, the spread of the rates and the work to do */
    _summary = () => {

        /* Fields */
        const { statistics, distribution, rate } = this.state

        if (!statistics) {
            return null
        }

        const top = Math.max(...distribution.map(item => item.count), 1)

        /* The rating of the last month against the month before it */
        let trend = null

        if (statistics.monthAverage && statistics.previousAverage) {
            const delta = statistics.monthAverage - statistics.previousAverage
            trend = { delta, up: delta >= 0 }
        }

        const tiles = [
            { key: "unanswered", field: "view", icon: "support", value: statistics.unanswered, text: "без ответа точки", accent: "blue" },
            { key: "photos", field: "view", icon: "image", value: statistics.photos, text: "с фотографиями", accent: "violet" },
            { key: "complaints", field: "view", icon: "alert", value: statistics.complaints, text: "жалоб ждут решения", accent: "orange" },
            { key: "manual", field: "source", icon: "edit", value: statistics.manual, text: "добавлены вручную", accent: "grey" }
        ]

        return (
            <div className="review-summary">

                <div className="review-score">

                    <div className="review-score-top">
                        <b>{ratingOf(statistics.average)}</b>
                        <div>
                            <Stars value={statistics.average} size={20} className="light" />
                            <span>{statistics.total.toLocaleString("ru-RU")} {plural(statistics.total, "отзыв", "отзыва", "отзывов")} на платформе</span>
                        </div>
                    </div>

                    <div className="review-score-chips">
                        <div className="review-score-chip">
                            <Icon name="trend" size={13} width={2.2} />
                            +{statistics.month.toLocaleString("ru-RU")} за 30 дней
                        </div>
                        {trend &&
                            <div className={`review-score-chip ${trend.up ? "" : "down"}`} title={`Средняя за 30 дней ${ratingOf(statistics.monthAverage)}, за месяц до этого ${ratingOf(statistics.previousAverage)}`}>
                                <Icon name={trend.up ? "up" : "down"} size={13} width={2.4} />
                                {trend.up ? "+" : "−"}{Math.abs(trend.delta).toFixed(2).replace(".", ",")} к прошлому месяцу
                            </div>
                        }
                        {!trend && statistics.monthAverage &&
                            <div className="review-score-chip">★ {ratingOf(statistics.monthAverage)} за месяц</div>
                        }
                    </div>

                </div>

                <div className="review-spread">
                    {distribution.map(item => {

                        const share = statistics.total > 0 ? Math.round(item.count / statistics.total * 100) : 0

                        return (
                            <div
                                key={`${item.rate}`}
                                onClick={() => this.toggle("rate", `${item.rate}`, "")}
                                className={`review-spread-row ${rateAccentOf(item.rate)} ${`${rate}` === `${item.rate}` ? "active" : ""}`}
                                title={RATE_WORDS[item.rate]}
                            >
                                <div className="review-spread-rate">{item.rate}<i>★</i></div>
                                <div className="review-spread-bar"><i style={{ width: `${Math.max(item.count > 0 ? 2 : 0, item.count / top * 100)}%` }} /></div>
                                <div className="review-spread-count">
                                    <b>{item.count.toLocaleString("ru-RU")}</b>
                                    <span>{share}%</span>
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div className="user-summary-tiles">
                    {tiles.map(tile => {

                        const active = this.state[tile.field] === tile.key

                        return (
                            <div
                                key={tile.key}
                                onClick={() => this.toggle(tile.field, tile.key, "all")}
                                className={`user-tile review-tile ${tile.accent} clickable ${active ? "active" : ""} ${tile.key === "complaints" && tile.value > 0 ? "alarm" : ""}`}
                            >
                                <div className="user-tile-icon"><Icon name={tile.icon} size={16} width={2} /></div>
                                <b>{tile.value.toLocaleString("ru-RU")}</b>
                                <span>{tile.text}</span>
                            </div>
                        )
                    })}
                </div>

            </div>
        )
    }

    /* The complaints of the points wait for a decision — the page says it first */
    _banner = () => {

        /* Fields */
        const { statistics, view } = this.state

        if (!statistics || statistics.complaints === 0 || view === "complaints") {
            return null
        }

        return (
            <div className="service-banner review-banner">
                <div className="service-banner-icon">
                    <Icon name="alert" size={22} width={1.8} />
                </div>
                <div className="service-banner-text">
                    <b>{statistics.complaints} {plural(statistics.complaints, "отзыв ждёт", "отзыва ждут", "отзывов ждут")} решения по жалобе точки</b>
                    <span>Точка считает отзыв нечестным. Примите жалобу — отзыв уйдёт с платформы, или отклоните — он останется.</span>
                </div>
                <div onClick={() => this.refilter({ view: "complaints" })} className="service-banner-button">Разобрать</div>
            </div>
        )
    }

    /* The points whose clients put the most of the bad rates lately */
    _attention = () => {

        /* Fields */
        const { attention, organization } = this.state

        if (attention.length === 0 || organization) {
            return null
        }

        return (
            <div className="user-channels-strip review-attention">
                <div className="user-channels-strip-title">Обратить внимание<em>1–2 ★ за 90 дней</em></div>
                <div className="review-attention-list">
                    {attention.map(item =>
                        <div key={`${item.id}`} onClick={event => this.pickOrganization(event, item)} className="review-attention-item" title={`${item.low} из ${item.total} отзывов за 90 дней — с оценкой 1 или 2`}>
                            <Avatar className="review-attention-avatar" src={item.image ? `${env.mediapoint}organizations/${item.image}` : null} alt={item.name}>
                                {organizationIcon()}
                            </Avatar>
                            <span>{item.name}</span>
                            <b>{item.low}</b>
                            <em>★ {ratingOf(item.average)}</em>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    _toolbar = () => {

        /* Fields */
        const { view, source, rate, organization, sort, count, statistics } = this.state

        return (
            <div className="service-toolbar">

                <div className="service-segment">
                    {VIEWS.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ view: item.key })} className={view === item.key ? "active" : ""}>
                            {item.text}
                            {item.key === "complaints" && statistics && statistics.complaints > 0 && <span>{statistics.complaints}</span>}
                        </div>
                    )}
                </div>

                <div className="service-segment">
                    {SOURCES.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ source: item.key })} className={source === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

                {rate !== "" &&
                    <div className="service-filter active">
                        <span className="review-filter-star">★</span>
                        {rate === "low" ? "1–2 звезды" : `${rate} ${plural(parseInt(rate), "звезда", "звезды", "звёзд")}`}
                        <div onClick={() => this.refilter({ rate: "" })} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                    </div>
                }

                {organization &&
                    <div className="service-filter active">
                        <Icon name="organization" size={15} width={2} />
                        {organization.name}
                        {organization.average > 0 && <em className="review-filter-rating">★ {ratingOf(organization.average)}</em>}
                        <div onClick={() => this.refilter({ organization: null })} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                    </div>
                }

                <div className="user-count">{count.toLocaleString("ru-RU")} {plural(count, "отзыв", "отзыва", "отзывов")}</div>

                <div className="service-sort">
                    <Icon name="sort" size={15} width={1.9} />
                    {SORTS.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ sort: item.key })} className={sort === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

            </div>
        )
    }

    /* One review of the list */
    _item = item => {

        const author = authorOf(item)
        const images = imagesOf(item.images)
        const accent = rateAccentOf(item.rate)
        const complaint = item.complaint !== null && item.complaint !== undefined ? COMPLAINT_STATUSES[item.complaint] : null
        const avatar = item.userImage ? `${env.mediapoint}users/${item.userImage}` : null

        /* Where the review comes from */
        let source = { text: "Без записи", icon: "review" }

        if (!item.userID) {
            source = { text: "Добавлен вручную", icon: "edit" }
        }
        else if (item.bookingID) {
            source = { text: "После визита", icon: "booking" }
        }

        return (
            <div key={`${item.id}`} onClick={() => this._review.current.open(item)} className={`review-card ${accent} ${complaint && parseInt(item.complaint) === 0 ? "complained" : ""}`}>

                <div className="review-card-head">

                    <div className="review-card-avatar-wrap" style={{ "--avatar": avatarColorOf(item.userID ? item.userID : item.id) }}>
                        <Avatar className="review-card-avatar" src={avatar} alt={author}>
                            {author ? <span>{initialsOf(author)}</span> : <Icon name="user" size={18} width={2} />}
                        </Avatar>
                    </div>

                    <div className="review-card-author">
                        <b>{author ? author : "Без имени"}</b>
                        <span><Icon name={source.icon} size={11} width={2.2} />{source.text} · {ago(item.createdAt)}</span>
                    </div>

                    <div className={`review-card-rate ${accent}`} title={RATE_WORDS[item.rate]}>
                        <Stars value={item.rate} size={13} />
                        <b>{item.rate}</b>
                    </div>

                </div>

                {item.comment && `${item.comment}`.trim().length > 0
                    ? <p className="review-card-text">{item.comment}</p>
                    : <p className="review-card-text empty">Только оценка, без текста</p>
                }

                {images.length > 0 &&
                    <div className="review-card-photos">
                        {images.slice(0, 4).map((image, index) =>
                            <div key={image} className="review-card-photo">
                                <img src={photoOf(image)} alt="Фото отзыва" loading="lazy" />
                                {index === 3 && images.length > 4 && <em>+{images.length - 4}</em>}
                            </div>
                        )}
                    </div>
                }

                {item.reply && `${item.reply}`.trim().length > 0 &&
                    <div className="review-card-reply">
                        <div className="review-card-reply-head"><Icon name="support" size={12} width={2.2} />Ответ точки{item.replier ? ` · ${item.replier}` : ""}</div>
                        <div className="review-card-reply-text">{item.reply}</div>
                    </div>
                }

                <div className="review-card-footer">

                    <div onClick={event => this.pickOrganization(event, { id: item.organizationID, name: item.organization, image: item.organizationImage })} className="review-card-point" title="Все отзывы точки">
                        <Avatar className="review-card-point-avatar" src={item.organizationImage ? `${env.mediapoint}organizations/${item.organizationImage}` : null} alt={item.organization}>
                            {organizationIcon()}
                        </Avatar>
                        <span>{item.organization ? item.organization : "Точка удалена"}</span>
                    </div>

                    {item.member && <div className="review-card-member"><Icon name="user" size={12} width={2.2} />{item.member}</div>}

                    {complaint && <div className={`user-badge ${complaint.accent} review-card-complaint`}><Icon name="alert" size={11} width={2.4} />{complaint.short}</div>}

                    {!item.reply && <div className="review-card-unanswered" title="Точка ещё не ответила">Нет ответа</div>}

                </div>

            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, data, count, page, limit } = this.state

        if (status !== "ok" || data.length === 0) {
            return (
                <div className="page-content">
                    {this._summary()}
                    {this._banner()}
                    {this._attention()}
                    {this._toolbar()}
                    <DataLayout status={status} reload={() => this.load(1, true)} data={data} onReset={() => this.refilter({ ...INITIAL })} />
                </div>
            )
        }

        return (
            <div className="page-content">

                {this._summary()}
                {this._banner()}
                {this._attention()}
                {this._toolbar()}

                <div className="review-grid">
                    {data.map(item => this._item(item))}
                </div>

                <Pagination size={limit} page={page} total={count} onPageChange={page => { window.scrollTo(0, 0); this.load(page, true) }} />

            </div>
        )
    }

    render = () => {
        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <ReviewModal
                    ref={this._review}
                    onChanged={() => this.load(this.state.page)}
                    onEdit={review => this._form.current.open(review)}
                    onClient={this.openClient}
                    onOrganization={organization => { this._review.current.close(); this.pickOrganization(null, organization) }}
                />

                <ReviewForm ref={this._form} onSaved={() => { this.load(this.state.page); this._review.current.reload() }} />

                <UserModal ref={this._user} onChanged={() => { }} />

            </div>
        )
    }
}

export default Reviews
